// ======================================================================
// 📁 File: src/App.js
// 📝 Purpose: Defines all application routes including Dashboard, Setup,
// Visitor Module, and newly added Ticket Module.
// 🕒 Updated on: 2025-11-29 19:52 IST
// ======================================================================

import React, { useContext, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { AuthProvider, AuthContext } from "./context/AuthContext";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import AllTasks from "./components/AllTasks";
import CreateTask from "./components/CreateTask";
import Reports from "./components/Reports/Reports";
import MyTeam from "./components/MyTeam";
import VerifyOtp from "./components/VerifyOtp";

import "bootstrap/dist/css/bootstrap.min.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Layouts
import DashboardLayout from "./layouts/DashboardLayout";
import SetupLayout from "./layouts/SetupLayout";

import "./i18n"; // this will run src/i18n/index.js and initialize i18next

// Setup Pages
import Users from "./components/Users";
import TenantList from "./components/tenant/TenantList";
import LocationList from "./components/location/LocationList";
import DepartmentList from "./components/departments/DepartmentList";
//import UserSecurityPage from "./modules/setup/user_security";
import UserSecurityPage from "./modules/setup/user_security/pages/UserSecurityPage";

import RoleMasterList from "./components/rolemaster/RoleMasterList";

// Functionality & Role Permissions
import FunctionalityList from "./components/functionality/FunctionalityList";
import RoleMasterPermissionMatrix from "./components/permissions/RoleMasterPermissionMatrix";
import TenantRolePermissionMatrix from "./components/permissions/TenantRolePermissionMatrix";
import LocationRolePermissionMatrix from "./components/permissions/LocationRolePermissionMatrix";

// Escalation Config
import EscalationRules from "./components/admin/escalation/EscalationRules";
import NotificationChannels from "./components/admin/escalation/NotificationChannels";
import UserNotificationPreferences from "./components/admin/escalation/UserNotificationPreferences";
import EscalationHistory from "./components/admin/escalation/EscalationHistory";

// Visitor Module Routes
import VisitorRoutes from "./modules/visitor/routes/visitorRoutes";

// ⭐ New Ticket Module Routes (Added 2025-11-29 19:52 IST)
import TicketRoutes from "./modules/ticket/routes/TicketRoutes";

// ⭐ Invoice Module Routes (Added 2025-12-03)
import InvoiceRoutes from "./modules/invoice/routes/InvoiceRoutes";

// ⭐ PPE Module Routes
import PPERoutes from "./modules/ppe/routes/PPERoutes";
// ⭐ IPE Module Routes
import IPERoutes from "./modules/ipe/routes/IPERoutes";

import VendorQuoteEntry from "./modules/ppe/pages/procurement/rfq/view/VendorQuoteEntry";

// ⭐ SERVICE CRM MODULE
import ServiceCRMRoutes from "./modules/service_crm/routes/ServiceCRMRoutes";

// ⭐ WMS MODULE
import WMSRoutes from "./modules/wms/routes/WMSRoutes";

// ⭐ EXPENSE MANAGEMENT MODULE
import ExpenseManagementRoutes from "./modules/expense_management/routes/ExpenseManagementRoutes";

// ⭐ CAMPAIGN MODULE
import CampaignRoutes from "./modules/campaign/routes/CampaignRoutes";

// ⭐ FINANCE MODULE
import FinanceRoutes from "./modules/finance/routes/FinanceRoutes";

// ELOSCA CRM — Enquiry Management

import EloscaCRMRoutes from "./modules/elosca_crm/routes/EloscaCRMRoutes";

// Trading Enquiry Module
import TradingRoutes from "./modules/trading/routes/TradingRoutes";

// Certificate Management Module
import CertificateRoutes from "./modules/asset/certificate_management/routes/CertificateRoutes";
import CertificateLayout from "./modules/asset/layout/CertificateLayout";

// Zyra AI Integration
import ZyraAIBubble from "./modules/zyra_ai/components/ZyraAIBubble";
import "./modules/zyra_ai/styles/zyraAi.css";
import "./modules/zyra_ai/styles/zyraAiDrawer.css";
import ZyraAIDrawer from "./modules/zyra_ai/components/ZyraAIDrawer";
import "./modules/zyra_ai/styles/aiQuickActions.css";
import "./modules/zyra_ai/styles/aiMessageInput.css";
import "./modules/zyra_ai/styles/aiConversation.css";
import "./modules/zyra_ai/styles/aiTypingIndicator.css";
import ZyraProvider from "./modules/zyra_ai/context/ZyraProvider";




// ======================================================================
// 🔐 Protected Route Wrapper
// ======================================================================
function ProtectedRoute({ children, permission }) {
  const { user, hasPermission } = useContext(AuthContext);

  if (!user) return <Navigate to="/login" replace />;

  if (permission) {
    const [functionKey, action] = permission.split(":");
    if (!hasPermission(functionKey, action)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}


// ======================================================================
// 🚦 Main Route Definitions
// ======================================================================
function AppRouter() {
  return (
    <Routes>

      {/* Public */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/verify-otp" element={<VerifyOtp />} />

      {/* Dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Dashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Tasks */}
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AllTasks />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-task"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateTask />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Reports */}
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Reports />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* My Team */}
      <Route
        path="/myteam"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <MyTeam />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Setup Pages */}
      <Route
        path="/setup/users"
        element={
          <ProtectedRoute permission="USER_MANAGE:view">
            <SetupLayout>
              <Users />
            </SetupLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/user-security"
        element={
          <ProtectedRoute permission="USER_SECURITY:view">
            <SetupLayout>
              <UserSecurityPage />
            </SetupLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/setup/tenants"
        element={
          <ProtectedRoute permission="TENANT_MANAGE:view">
            <SetupLayout>
              <TenantList />
            </SetupLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/setup/locations"
        element={
          <ProtectedRoute permission="LOCATION_MANAGE:view">
            <SetupLayout>
              <LocationList />
            </SetupLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/setup/departments"
        element={
          <ProtectedRoute permission="DEPARTMENT_MANAGE:view">
            <SetupLayout>
              <DepartmentList />
            </SetupLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/setup/rolesmaster/platform"
        element={
          <ProtectedRoute permission="ROLE_MASTER_MANAGE:view">
            <SetupLayout>
              <RoleMasterList />
            </SetupLayout>
          </ProtectedRoute>
        }
      />

      {/* Functionality */}
      <Route
        path="/setup/functionality"
        element={
          <ProtectedRoute permission="FUNCTIONALITY_MANAGE:view">
            <SetupLayout>
              <FunctionalityList />
            </SetupLayout>
          </ProtectedRoute>
        }
      />

      {/* Permission Matrices */}
      <Route
        path="/setup/role-master-permission"
        element={
          <ProtectedRoute permission="ROLE_MASTER_MANAGE:view">
            <SetupLayout>
              <RoleMasterPermissionMatrix />
            </SetupLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/setup/tenant-role-permission/:tenantId"
        element={
          <ProtectedRoute permission="ROLE_MANAGE:view">
            <SetupLayout>
              <TenantRolePermissionMatrix />
            </SetupLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/setup/location-role-permission"
        element={
          <ProtectedRoute permission="LOCATION_ROLE_PERMISSION_MANAGE:view">
            <SetupLayout>
              <LocationRolePermissionMatrix />
            </SetupLayout>
          </ProtectedRoute>
        }
      />

      {/* Escalation */}
      <Route
        path="/setup/escalation/rules"
        element={
          <SetupLayout>
            <EscalationRules />
          </SetupLayout>
        }
      />

      <Route
        path="/setup/escalation/channels"
        element={
          <SetupLayout>
            <NotificationChannels />
          </SetupLayout>
        }
      />

      <Route
        path="/setup/escalation/preferences"
        element={
          <SetupLayout>
            <UserNotificationPreferences />
          </SetupLayout>
        }
      />

      <Route
        path="/setup/escalation/history"
        element={
          <SetupLayout>
            <EscalationHistory />
          </SetupLayout>
        }
      />

      {/* ⭐ Visitor Management Module */}
      <Route
        path="/visitor/*"
        element={
          <ProtectedRoute permission="VISITOR_DASHBOARD:view">
            <VisitorRoutes />
          </ProtectedRoute>
        }
      />

      {/* ⭐⭐ NEW — Ticket Management Module (Added 2025-11-29 19:52 IST) */}
      <Route
        path="/tickets/*"
        element={
          <ProtectedRoute permission="TICKET_VIEW:view">
            <TicketRoutes />
          </ProtectedRoute>
        }
      />

      {/* ⭐ NEW — Invoice Management Module (Added 2025-12-03) */}
<Route
  path="/invoices/*"
  element={
    <ProtectedRoute permission="INVOICE_VIEW:view">
      <InvoiceRoutes />
    </ProtectedRoute>
  }
/>

{/* ================= PUBLIC VENDOR QUOTE PORTAL ================= */}
<Route
  path="/vendor/rfq/submit"
  element={<VendorQuoteEntry />}
/>

{/* ⭐ PPE Module */}
      <Route
        path="/ppe/*"
        element={
          <ProtectedRoute permission="PPE_VIEW_DASHBOARD:view">
            <PPERoutes />
          </ProtectedRoute>
        }
      />

{/* ⭐ IPE Module */}
<Route
  path="/ipe/*"
  element={
    <ProtectedRoute permission="PPE_VIEW_DASHBOARD:view">
      <IPERoutes />
    </ProtectedRoute>
  }
/>

{/* ⭐ SERVICE CRM MODULE SERVICE_CRM_DASHBOARD */}
<Route
  path="/service-crm/*"
  element={
    //<ProtectedRoute permission="PPE_VIEW_DASHBOARD:view">
      //<ProtectedRoute permission="SERVICE_TICKET:view">
      <ProtectedRoute permission="SCM_DASHBOARD:view">     
      <ServiceCRMRoutes />
    </ProtectedRoute>
  }
/>
{/* ⭐ WMS MODULE */}
<Route
  path="/wms/*"
  element={
    <ProtectedRoute permission="WMS_DASHBOARD:view">
      <WMSRoutes />
    </ProtectedRoute>
  }
/>

{/* ⭐ EXPENSE MANAGEMENT MODULE */}

<Route
  path="/expense-management/*"
  element={
    <ProtectedRoute permission="EXP_DASHBOARD:view">
      <ExpenseManagementRoutes />
    </ProtectedRoute>
  }
/>

{/* ⭐ FINANCE MODULE */}

<Route
  path="/finance/*"
  element={
    <ProtectedRoute permission="WMS_DASHBOARD:view">
      <FinanceRoutes />
    </ProtectedRoute>
  }
/>

{/* ELOSCA CRM MODULE */}
<Route
  path="/elosca-crm/*"
  element={
    <ProtectedRoute>
      <EloscaCRMRoutes />
    </ProtectedRoute>
  }
/>
{/* TRADING MODULE */}
<Route
  path="/trading/*"
  element={
    <ProtectedRoute>
      <TradingRoutes />
    </ProtectedRoute>
  }
/>


{/* CERTIFICATE MANAGEMENT MODULE */}
<Route
  path="/assets/certificate-management/*"
  element={
    <ProtectedRoute>
      <CertificateLayout>
        <CertificateRoutes />
      </CertificateLayout>
    </ProtectedRoute>
  }
/>


{/* ⭐ CAMPAIGN MODULE */}

<Route
  path="/campaign/*"
  element={
    <ProtectedRoute permission="CAMPAIGN_DASHBOARD:view">
      <CampaignRoutes />
    </ProtectedRoute>
  }
/>


      {/* Fallback */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}


// ======================================================================
// 🌐 App Root
// ======================================================================
export default function App() {
  const [zyraAIOpen, setZyraAIOpen] = useState(false);
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />

         {/* ✦ GLOBAL ZYRA AI ASSISTANT */}
        

        {/* ✅ GLOBAL TOAST RENDERER (REQUIRED) */}
        <ToastContainer
          position="top-right"
          autoClose={3000}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable
          theme="colored"
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
